// BaseSceneIntegration.js
// Integration layer for BaseMenuScene to use new enterprise services
// Provides standardized initialization and cleanup for all template scenes

import { navigationHandler } from './NavigationActionHandler.js';
import { eventBindingSystem } from './UnifiedEventBindingSystem.js';
import { professionalAnimations } from './ProfessionalAnimationSystem.js';
import { unifiedCSSManager } from './UnifiedCSSManager.js';
import { professionalNotifications } from './ProfessionalNotificationSystem.js';
import { professionalLoading } from './ProfessionalLoadingSystem.js';
import { performanceMonitoring } from './PerformanceMonitoringSystem.js';
import { uxPolish } from './UXPolishSystem.js';
import DesignTokenIntegration from './DesignTokenIntegration.js';
import { domLifecycleManager } from './DOMLifecycleManager.js';
import sceneManager from '../utils/SceneManager.js';

/**
 * Base scene integration service
 * Provides standardized methods for template scenes to use enterprise services
 */
export class BaseSceneIntegration {
    static _instance = null;
    
    static getInstance() {
        if (!BaseSceneIntegration._instance) {
            BaseSceneIntegration._instance = new BaseSceneIntegration();
        }
        return BaseSceneIntegration._instance;
    }
    
    constructor() {
        if (BaseSceneIntegration._instance) {
            return BaseSceneIntegration._instance;
        }
        
        this.initializedScenes = new Set();
        BaseSceneIntegration._instance = this;
    }
    
    /**
     * Initialize a template scene with enterprise services
     * Call this in scene's create() method
     * @param {BaseMenuScene} scene - Scene to initialize
     * @param {Object} options - Configuration options
     */
    initializeTemplateScene(scene, options = {}) {
        const sceneKey = scene.scene.key;
        
        if (this.initializedScenes.has(sceneKey)) {
            console.warn(`⚠️ Scene ${sceneKey} already initialized`);
            return;
        }
        
        console.log(`🚀 BaseSceneIntegration: Initializing ${sceneKey}`);
        
        const config = {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true,
            sceneType: this._detectSceneType(sceneKey),
            ...options
        };
        
        // Load optimized CSS for scene
        unifiedCSSManager.loadSceneCSS(sceneKey, config.sceneType);
        
        // ENTERPRISE INTEGRATION: Use DOMLifecycleManager for proper scene preparation
        domLifecycleManager.prepareSceneTransition('unknown', 'dom', sceneKey);
        
        // Set up navigation handler context
        if (config.enableNavigation) {
            navigationHandler.setCurrentScene(scene);
        }
        
        // Bind events after a brief delay to ensure DOM is ready
        if (config.bindEvents) {
            setTimeout(() => {
                eventBindingSystem.bindSceneEvents(scene, config.overlayId);
                
                // Apply professional animations to the scene
                this._enhanceSceneAnimations(scene, config.overlayId);
                
                // Apply UX polish and micro-interactions
                uxPolish.polishScene(sceneKey, config.overlayId);
                
                // Apply _ui_upgrade design token compliance
                const overlay = document.getElementById(config.overlayId);
                if (overlay) {
                    DesignTokenIntegration.applyComponentClasses(overlay);
                }
                
                // Start performance monitoring if not already active
                if (!performanceMonitoring.isMonitoring) {
                    performanceMonitoring.startMonitoring();
                }
                
                // Complete scene transition setup
                domLifecycleManager.completeSceneTransition('dom', sceneKey);
            }, 100);
        }
        
        // Override scene's startScene method to use standardized transitions
        this._enhanceSceneTransitions(scene);
        
        // Add cleanup handler
        scene.events.once('shutdown', () => {
            this.cleanupTemplateScene(scene, config);
        });
        
        this.initializedScenes.add(sceneKey);
        console.log(`✅ Scene ${sceneKey} initialization complete`);
    }
    
    /**
     * Cleanup a template scene
     * Automatically called on scene shutdown
     * @param {BaseMenuScene} scene - Scene to cleanup
     * @param {Object} config - Configuration used during initialization
     */
    cleanupTemplateScene(scene, config = {}) {
        const sceneKey = scene.scene.key;
        
        console.log(`🧹 BaseSceneIntegration: Cleaning up ${sceneKey}`);
        
        // Unbind events
        if (config.bindEvents !== false) {
            eventBindingSystem.unbindSceneEvents(scene, config.overlayId || 'auth-menu-dom-overlay');
        }
        
        // Cleanup CSS
        unifiedCSSManager.cleanupSceneCSS(sceneKey);
        
        // Remove UX polish
        uxPolish.removePolish(sceneKey);
        
        // Reset navigation handler if this was the current scene
        if (navigationHandler.currentScene === scene) {
            navigationHandler.setCurrentScene(null);
        }
        
        this.initializedScenes.delete(sceneKey);
        console.log(`✅ Scene ${sceneKey} cleanup complete`);
    }
    
    /**
     * Enhance scene's transition methods to use standardized system
     * @private
     */
    _enhanceSceneTransitions(scene) {
        // Store original startScene method if it exists
        const originalStartScene = scene.startScene;
        
        // Override with standardized transition
        scene.startScene = (targetScene, data = {}) => {
            console.log(`🔄 Enhanced transition: ${scene.scene.key} → ${targetScene}`);
            
            // Use SceneManager for consistent transitions
            try {
                if (sceneManager && sceneManager.switchToScene) {
                    sceneManager.switchToScene(targetScene, data);
                } else if (originalStartScene) {
                    // Fallback to original method
                    originalStartScene.call(scene, targetScene, data);
                } else {
                    // Last resort: direct Phaser transition
                    scene.scene.start(targetScene, data);
                }
            } catch (error) {
                console.error(`🚨 Transition failed: ${scene.scene.key} → ${targetScene}`, error);
                
                // Emergency fallback
                if (originalStartScene) {
                    originalStartScene.call(scene, targetScene, data);
                } else {
                    scene.scene.start(targetScene, data);
                }
            }
        };
        
        // Add convenience method for navigation actions
        scene.handleTemplateAction = (action) => {
            return navigationHandler.handleTemplateAction(action, scene);
        };
        
        // Add convenience method for game actions
        scene.handleGameAction = (game) => {
            return navigationHandler.handleGameAction(game, scene);
        };
    }
    
    /**
     * Check if a scene is initialized with enterprise services
     * @param {string} sceneKey - Scene key to check
     * @returns {boolean} - Whether scene is initialized
     */
    isSceneInitialized(sceneKey) {
        return this.initializedScenes.has(sceneKey);
    }
    
    /**
     * Get list of initialized scenes
     * @returns {Array<string>} - Array of scene keys
     */
    getInitializedScenes() {
        return Array.from(this.initializedScenes);
    }
    
    /**
     * Enhance scene with professional animations
     * @private
     */
    _enhanceSceneAnimations(scene, overlayId) {
        const overlay = document.getElementById(overlayId);
        if (!overlay) return;
        
        // Animate scene entrance
        professionalAnimations.animateSceneTransition(overlayId, 'in');
        
        // Enhance buttons with professional styling
        const buttons = overlay.querySelectorAll('button, .btn, .nav-item, .header-btn');
        buttons.forEach(button => {
            professionalAnimations.enhanceButton(button);
        });
        
        // Apply stagger reveal to navigation items
        professionalAnimations.animateStaggerReveal('.nav-menu', '.nav-item');
        
        // Apply content reveal to main content areas
        const contentAreas = overlay.querySelectorAll('.kpi-grid, .charts-row, .game-grid, .leaderboard-container');
        contentAreas.forEach(area => {
            area.classList.add('content-reveal');
        });
        
        console.log(`✨ Professional animations applied to ${scene.scene.key}`);
    }
    
    /**
     * Detect scene type for CSS optimization
     * @private
     */
    _detectSceneType(sceneKey) {
        const sceneTypeMap = {
            'AnalyticsSceneDOM': 'analytics',
            'HallOfFameDesktopTemplate': 'hall-of-fame',
            'GameCenterSceneDOM': 'game-center',
            'ProfileSceneDOMTemplate': 'profile',
            'AdminPanelSceneDOM': 'admin-panel',
            'NotificationsSceneDOM': 'notifications'
        };
        
        return sceneTypeMap[sceneKey] || 'dashboard';
    }
    
    /**
     * Reset all scene integrations (for debugging)
     */
    resetAllIntegrations() {
        console.log('🔄 BaseSceneIntegration: Resetting all integrations');
        this.initializedScenes.clear();
        eventBindingSystem.resetAllBindings();
        navigationHandler.resetTransitionState();
        unifiedCSSManager.resetCSSManagement();
        uxPolish.resetPolish();
        performanceMonitoring.clearMetrics();
    }
}

// Export singleton instance
export const baseSceneIntegration = BaseSceneIntegration.getInstance();
export default baseSceneIntegration;
